import { strings } from "../../../../../util/strings";
import type { DataMutate, IAddress, Tenant } from "../../../../../types/types";
import type { FunctionComponent } from "react";
import { useContext, useState } from "react";
import {
  PrimaryButtonMediumV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import { PlusIcon } from "../../../../../components/Icons/Icons";
import {
  Notifications,
  useNotifications,
} from "../../../../../components/Notifications/NotificationsContext";
import Axios from "axios";
import { useValidateEmail } from "../../../../../util/useValidateEmail";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { ToggleGroup } from "../../../../../components/ToggleGroups/ToggleGroups";
import { LocationsList } from "../../../../../components/LocationsList/LocationsList";
import type { LocationFormPOCInput, LocationInputs } from "./LocationForm";
import { LocationForm } from "./LocationForm";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../../../components/Modal/Modal";
import styled from "styled-components";
import { H3 } from "../../../../../components/Typography/Typography";
import { Row } from "../../../../../components/Layout/Layout";

interface PointOfContactPayload {
  poc_type: "manual" | "existing";
  contact_first_name?: string;
  contact_last_name?: string;
  email_address?: string;
  phone_number?: string;
  point_of_contact_user_id?: string;
}

interface LocationData
  extends Omit<LocationInputs, "country" | "state" | "points_of_contact"> {
  type: "Warehouse";
  country: string;
  state: string;
  points_of_contact: PointOfContactPayload[];
}

export interface IAddressDetails {
  has_transaction: boolean;
  has_price_tiers: boolean;
}

interface EditLocationsProps {
  addresses: IAddress[];
  tenantId: string;
  hqAddress?: IAddress;
  mutateTenant: DataMutate<Tenant>;
}

const ModalWrapper = styled.div`
  padding: 50px 90px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.small};
`;

const WarningTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.large};
  color: ${({ theme }) => theme.warningTextColor};
  margin-bottom: 30px;
`;

const LocationsToggleGroupWrapper = styled.div`
  width: fit-content;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const isManualPoc = (
  poc: LocationFormPOCInput
): poc is LocationFormPOCInput & { poc_type: "manual" } =>
  poc.poc_type === "manual";

const toPointsOfContactPayload = (
  pocs: LocationFormPOCInput[]
): PointOfContactPayload[] =>
  pocs.map((poc) =>
    isManualPoc(poc)
      ? {
          poc_type: "manual",
          contact_first_name: poc.contact_first_name,
          contact_last_name: poc.contact_last_name,
          email_address: poc.email_address,
          phone_number: `${poc.country_code?.value ?? ""}${
            poc.phone_number ?? ""
          }`,
        }
      : {
          poc_type: "existing",
          point_of_contact_user_id: poc.point_of_contact_id?.value,
        }
  );

/** Validates every manually-entered POC's email; true only if all pass. */
const validateManualPocEmails = async (
  pocs: LocationFormPOCInput[],
  validateEmail: (args: { email: string; source: string }) => Promise<boolean>,
  source: string
) => {
  const manualEmails = pocs
    .filter(isManualPoc)
    .map((poc) => poc.email_address)
    .filter((email): email is string => Boolean(email));
  const results = await Promise.all(
    manualEmails.map((email) => validateEmail({ email, source }))
  );
  return results.every(Boolean);
};

/**
 * A hook to add location.
 * It returns a function, when called, returns details of new address added
 */
export const useAddLocation = ({
  buyer_tenant_id,
}: {
  buyer_tenant_id: string;
}) => {
  const { validateEmail } = useValidateEmail();
  const { t } = useTranslation();
  const { notifyError } = useNotifications();

  const addLocation = async ({ inputs }: { inputs: LocationInputs }) => {
    const emailsValid = await validateManualPocEmails(
      inputs.points_of_contact,
      validateEmail,
      "manage company info locations edit"
    );
    if (!emailsValid) return undefined;

    const newAddress: LocationData = {
      ...inputs,
      type: "Warehouse",
      state: inputs.state.value,
      country: inputs.country.value,
      points_of_contact: inputs.same_as_hq
        ? []
        : toPointsOfContactPayload(inputs.points_of_contact),
    };

    try {
      const { data } = await Axios.post<IAddress>(
        `/v1/tenants/${buyer_tenant_id}/addresses`,
        newAddress
      );
      return data;
    } catch (error) {
      notifyError(strings(t).submitError, { error });
    }
  };
  return addLocation;
};

/**
 * The "Shipping Locations" section on the CompanyInfo page. Always shows the
 * active/inactive location stack; adding and editing each address happens in
 * a SlideOut.
 */
export const EditLocations: FunctionComponent<EditLocationsProps> = ({
  addresses,
  tenantId,
  mutateTenant,
  hqAddress,
}) => {
  const { t } = useTranslation();
  const { validateEmail } = useValidateEmail();
  const addLocation = useAddLocation({ buyer_tenant_id: tenantId });
  const { notifyError, notifySuccess } = useContext(Notifications);

  const activeLabel = t("Active");
  const inactiveLabel = t("Inactive");
  const activeAddresses = addresses.filter((address) => address.is_active);
  const inactiveAddresses = addresses.filter((address) => !address.is_active);

  const [showAddLocation, setShowAddLocation] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<IAddress>();
  const [editTaxCRM, setEditTaxCRM] = useState<boolean>(false);
  const [addressDetailsToEdit, setAddressDetailsToEdit] =
    useState<IAddressDetails>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(
    activeAddresses.length > 0 ? activeLabel : inactiveLabel
  );

  const onAddLocation = async (inputs: LocationInputs) => {
    const newLocation = await addLocation({ inputs });
    if (newLocation) {
      await mutateTenant();
      setShowAddLocation(false);
      return true;
    }
    return false;
  };

  const onEditAddress = async (address: IAddress) => {
    try {
      const addressDetails = await Axios.get<IAddressDetails>(
        `/v1/tenants/${tenantId}/addresses/${address.id}/details`
      );
      setAddressDetailsToEdit(addressDetails.data);
      if (addressDetails.data.has_transaction) {
        if (
          !address.tax_id ||
          address.tax_id === "" ||
          !address.crm_id ||
          address.crm_id === ""
        ) {
          setEditTaxCRM(true);
          setAddressToEdit(address);
        } else {
          setEditTaxCRM(false);
          setShowConfirmationModal(true);
        }
      } else {
        setEditTaxCRM(false);
        setAddressToEdit(address);
      }
    } catch (error) {
      notifyError(t("An error occurred"), { error });
    }
  };

  const onEditAddressSave = async (inputs: LocationInputs) => {
    if (!addressToEdit) return false;

    const emailsValid = await validateManualPocEmails(
      inputs.points_of_contact,
      validateEmail,
      "manage company info locations edit"
    );
    if (!emailsValid) return false;

    const newAddress: LocationData = {
      ...inputs,
      type: "Warehouse",
      state: inputs.state.value,
      country: inputs.country.value,
      points_of_contact: inputs.same_as_hq
        ? []
        : toPointsOfContactPayload(inputs.points_of_contact),
    };

    try {
      await Axios.patch(
        `/v1/tenants/${tenantId}/addresses/${addressToEdit.id}`,
        newAddress
      );
      setAddressToEdit(undefined);
      // Refresh tenant data to refresh the locations table.
      await mutateTenant();
      notifySuccess(t("Address has been updated successfully"));
      return true;
    } catch (error) {
      notifyError(strings(t).submitError, { error });
    }
    return false;
  };

  const onDeleteAddress = async (id: string) => {
    try {
      await Axios.delete(`/v1/tenants/${tenantId}/addresses/${id}`);
      // Refresh tenant data to refresh the locations table.
      await mutateTenant();
      notifySuccess(t("Address has been deleted successfully"));
    } catch (error) {
      notifyError(strings(t).submitError, { error });
    }
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  return (
    <>
      <Row>
        <H3>{t("Shipping Locations")}</H3>
        <PrimaryButtonMediumV2
          testid="add-location-button"
          type="button"
          onClick={() => setShowAddLocation(true)}
        >
          <PlusIcon />
          {t("Add Location")}
        </PrimaryButtonMediumV2>
      </Row>
      <LocationsToggleGroupWrapper>
        <ToggleGroup
          names={[activeLabel, inactiveLabel]}
          activeButton={selectedTab}
          clickHandler={(_, name) => setSelectedTab(name)}
          disabledButtons={[
            ...(activeAddresses.length === 0 ? [activeLabel] : []),
            ...(inactiveAddresses.length === 0 ? [inactiveLabel] : []),
          ]}
        />
      </LocationsToggleGroupWrapper>
      <LocationsList
        addresses={
          selectedTab === activeLabel ? activeAddresses : inactiveAddresses
        }
        onDeleteAddress={onDeleteAddress}
        onEditAddress={onEditAddress}
      />

      <SlideOut
        title={t("Add Location")}
        show={showAddLocation}
        closeFlyout={() => setShowAddLocation(false)}
      >
        <LocationForm
          onSubmitFn={onAddLocation}
          submitButtonText={t("Add")}
          tenantId={tenantId}
          hqAddress={hqAddress}
          onCancel={() => setShowAddLocation(false)}
        />
      </SlideOut>

      <SlideOut
        title={t("Edit Location")}
        show={!!addressToEdit}
        closeFlyout={() => setAddressToEdit(undefined)}
      >
        <LocationForm
          onSubmitFn={onEditAddressSave}
          submitButtonText={t("Save")}
          address={addressToEdit}
          editTaxCRM={editTaxCRM}
          addressDetails={addressDetailsToEdit}
          tenantId={tenantId}
          hqAddress={hqAddress}
          onCancel={() => setAddressToEdit(undefined)}
        />
      </SlideOut>
      <Modal
        overlay
        show={showConfirmationModal}
        closeModal={closeConfirmationModal}
        modalWidth={"680px"}
      >
        <ModalWrapper>
          <WarningTitle>
            {t(
              "This Shipping Location is currently used in an active transaction or sample request"
            )}
          </WarningTitle>
          <div>
            {t(
              "This location cannot be updated because it is currently in use on an active transaction or sample request. "
            )}
          </div>
          <div>
            {t(
              "You can edit this location after the transaction or sample request has been completed, or contact Support if you would like to edit this location now."
            )}
          </div>
          <TertiaryButtonLargeV2
            onClick={closeConfirmationModal}
            style={{ marginTop: "30px" }}
          >
            {t("Cancel")}
          </TertiaryButtonLargeV2>
        </ModalWrapper>
      </Modal>
    </>
  );
};
